import { QuizHandler } from "../../lib/quiz/Quiz";
import { useState, useEffect } from "react";
import { GiNotebook } from "react-icons/gi";
import { BsLink45Deg } from "react-icons/bs";
import LinkCard from "../LinkCard/LinkCard";
import "./QuizQuestion.scoped.scss";
import accent1 from "../../img/quizzes/accent_1.png";
import accent2 from "../../img/quizzes/accent_2.png";
import accent3 from "../../img/quizzes/accent_3.png";
import accent4 from "../../img/quizzes/accent_4.png";
import accent5 from "../../img/quizzes/accent_5.png";
import accent6 from "../../img/quizzes/accent_6.png";

interface Props {
  quizHandler: QuizHandler;
  position: number;
  questionIndex: number;
  showAnswer: boolean;
}

const accentImages = [accent1, accent2, accent3, accent4, accent5, accent6];

export default function QuizQuestion({
  quizHandler,
  position,
  questionIndex,
  showAnswer,
}: Props) {
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState<number | null>(
    null
  );
  const [accentImage, setAccentImage] = useState<string>();
  const questionNumber = position + 1;
  const quizMode = quizHandler.getCurrentMode();
  const question = quizHandler.getQuestion(questionIndex);

  useEffect(() => {
    setAccentImage(accentImages[questionNumber % accentImages.length]);
  }, [questionNumber]);

  const isCorrect = () => {
    if (selectedChoiceIndex === null) {
      return false;
    }
    const choice = question.choices[selectedChoiceIndex];
    return choice.isCorrect || false;
  };

  const getBgColorOfChoice = (choiceIndex: number): string => {
    const choice = question.choices[choiceIndex];
    if (showAnswer) {
      if (choice.isCorrect) {
        return "bg-green-200";
      } else if (selectedChoiceIndex === choiceIndex) {
        return "bg-red-200";
      } else {
        return "";
      }
    } else {
      if (selectedChoiceIndex === choiceIndex) {
        return "bg-blue-100 hover:bg-blue-200";
      } else {
        return "hover:bg-gray-100";
      }
    }
  };

  const selectChoice = (choiceIndex: number) => {
    setSelectedChoiceIndex(choiceIndex);
    quizHandler.addAnswer({
      quizMode: quizMode,
      questionIndex: questionIndex,
      choiceIndex: choiceIndex,
    });
  };

  return (
    <div className="relative p-5 mb-3 text-left bg-white rounded-xl md:flex">
      {!showAnswer && (
        <img
          src={accentImage}
          alt="accent"
          className={
            "absolute w-14 bottom-3 right-3 mix-blend-multiply " +
            (selectedChoiceIndex === null ? "opacity-30" : "opacity-80")
          }
        />
      )}

      <div className="md:absolute top-5 left-5">
        <div className="inline-block text-3xl font-extrabold text-pink-400">
          Q{questionNumber}
        </div>
        {showAnswer && (
          <div className="inline-block ml-3 text-3xl font-extrabold">
            {isCorrect() ? (
              <span className="text-blue-500">○</span>
            ) : (
              <span className="text-red-400">×</span>
            )}
          </div>
        )}
      </div>

      <div className="w-full text-gray-800 md:px-24">
        <div className="mt-1 mb-3">{question.question}</div>

        <div className="question-choices">
          {question.choices.map((choice, choiceIndex) => (
            <div
              key={choiceIndex}
              className={
                "rounded-md py-1 px-2 " + getBgColorOfChoice(choiceIndex)
              }
            >
              <label
                className={
                  "block w-full " + (showAnswer ? "" : "cursor-pointer")
                }
              >
                <input
                  type="radio"
                  name={String(questionNumber)}
                  value={choiceIndex}
                  disabled={showAnswer}
                  onChange={() => selectChoice(choiceIndex)}
                />
                <span className="ml-2">{choice.text}</span>
              </label>
            </div>
          ))}
        </div>

        {showAnswer && (
          <div className="question-explanation">
            <details className="py-2 text-base" open={!isCorrect()}>
              <summary className="p-1 text-lg cursor-pointer">
                解説を読む
              </summary>

              <div className="p-2 my-1 bg-gray-100 rounded-lg">
                {question.explanation && (
                  <>
                    <h3 className="block w-auto p-1 text-lg font-medium text-gray-600">
                      <GiNotebook /> 解説
                    </h3>
                    <p className="px-5 text-gray-800">{question.explanation}</p>
                  </>
                )}

                {question.related_urls.length > 0 && (
                  <>
                    <h3 className="block w-auto p-1 my-1 text-lg font-medium text-gray-600">
                      <BsLink45Deg /> 関連リンク
                    </h3>
                    <div className="px-5">
                      {question.related_urls
                        .filter((url) => url)
                        .map((url, index) => (
                          <LinkCard
                            url={url}
                            width={384}
                            height={216}
                            key={index}
                          ></LinkCard>
                        ))}
                    </div>
                  </>
                )}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
